/**
 * UCC Entities Hooks
 *
 * React Query hooks for secured party and debtor entity management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-entities';
import { uccEntitiesClient } from '../api/typed-clients';

/** Request bodies from schema-registry/ucc-entities.openapi.yaml. */
export type CreateSecuredPartyRequest = components['schemas']['CreateSecuredPartyRequest'];
export type UpdateSecuredPartyRequest = components['schemas']['UpdateSecuredPartyRequest'];
export type CreateDebtorRequest = components['schemas']['CreateDebtorRequest'];
export type UpdateDebtorRequest = components['schemas']['UpdateDebtorRequest'];
import { apiErrorMessage } from '../api/error-message';

const SECURED_PARTIES_KEY = 'uccSecuredParties';
const DEBTORS_KEY = 'uccDebtors';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Secured Parties
// ---------------------------------------------------------------------------

/** `enabled: false` defers the read, e.g. until a dialog that lists these rows opens. */
export function useSecuredParties(params: { tenantId: string; page?: number; limit?: number; enabled?: boolean }) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: [SECURED_PARTIES_KEY, query],
    queryFn: async () => {
      const result = await uccEntitiesClient.GET('/secured-parties', {
        params: { query: { tenantId: query.tenantId, page: query.page, limit: query.limit } },
      });
      return unwrap(result);
    },
    enabled: enabled && !!query.tenantId,
  });
}

export function useSecuredParty(securedPartyId: string | undefined) {
  return useQuery({
    queryKey: [SECURED_PARTIES_KEY, securedPartyId],
    queryFn: async () => {
      const result = await uccEntitiesClient.GET('/secured-parties/{securedPartyId}', {
        params: { path: { securedPartyId: securedPartyId! } },
      });
      return unwrap(result);
    },
    enabled: !!securedPartyId,
  });
}

export function useCreateSecuredParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSecuredPartyRequest) => {
      const result = await uccEntitiesClient.POST('/secured-parties', { body });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECURED_PARTIES_KEY] });
    },
  });
}

export function useUpdateSecuredParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ securedPartyId, body }: { securedPartyId: string; body: UpdateSecuredPartyRequest }) => {
      const result = await uccEntitiesClient.PUT('/secured-parties/{securedPartyId}', {
        params: { path: { securedPartyId } },
        body,
      });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECURED_PARTIES_KEY] });
    },
  });
}

export function useDeleteSecuredParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (securedPartyId: string) => {
      const result = await uccEntitiesClient.DELETE('/secured-parties/{securedPartyId}', {
        params: { path: { securedPartyId } },
      });
      if (result.error) {
        const errorBody = result.error as { message?: string } | undefined;
        throw new Error(apiErrorMessage(errorBody) ?? 'Failed to delete secured party');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECURED_PARTIES_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Debtors
// ---------------------------------------------------------------------------

/** `enabled: false` defers the read, e.g. until a dialog that lists these rows opens. */
export function useDebtors(params: { tenantId: string; page?: number; limit?: number; enabled?: boolean }) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: [DEBTORS_KEY, query],
    queryFn: async () => {
      const result = await uccEntitiesClient.GET('/debtors', {
        params: { query: { tenantId: query.tenantId, page: query.page, limit: query.limit } },
      });
      return unwrap(result);
    },
    enabled: enabled && !!query.tenantId,
  });
}

export function useDebtor(debtorId: string | undefined) {
  return useQuery({
    queryKey: [DEBTORS_KEY, debtorId],
    queryFn: async () => {
      const result = await uccEntitiesClient.GET('/debtors/{debtorId}', {
        params: { path: { debtorId: debtorId! } },
      });
      return unwrap(result);
    },
    enabled: !!debtorId,
  });
}

export function useCreateDebtor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDebtorRequest) => {
      const result = await uccEntitiesClient.POST('/debtors', { body });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEBTORS_KEY] });
    },
  });
}

export function useUpdateDebtor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ debtorId, body }: { debtorId: string; body: UpdateDebtorRequest }) => {
      const result = await uccEntitiesClient.PUT('/debtors/{debtorId}', {
        params: { path: { debtorId } },
        body,
      });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEBTORS_KEY] });
    },
  });
}

export function useDeleteDebtor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (debtorId: string) => {
      const result = await uccEntitiesClient.DELETE('/debtors/{debtorId}', {
        params: { path: { debtorId } },
      });
      if (result.error) {
        const errorBody = result.error as { message?: string } | undefined;
        throw new Error(apiErrorMessage(errorBody) ?? 'Failed to delete debtor');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEBTORS_KEY] });
    },
  });
}
